import {
  COMPANY_RESOURCE_MANAGEMENT_API,
  MESSAGES,
  PEOPLE_RESOURCE_MANAGEMENT_API,
} from '@aibees/elephant/constants';
import { SearchMode, SearchType } from '@aibees/elephant/types';
import { HttpMethod } from '@aibees/shared/definition';
import { useNotify } from '@aibees/shared/hooks';
import { useBaseMutation } from '@aibees/shared/http';
import { useWorkspace } from '@aibees/shared/workspace';
import { HttpStatusCode } from 'axios';
import { ERROR_CODES } from '../constants/error-codes';
import { SaveRecordRequest } from './types';
import { useInvalidateCompanyAndPeopleQueries } from './use-invalidate-company-and-people-queries';
import { useSearchSimilarityFromRoute } from './use-obtain-search-type-from-route';

export function useSaveRecord(type?: SearchType) {
  const notify = useNotify();
  const workspaceId = useWorkspace()?.id;
  const invalidateData = useInvalidateCompanyAndPeopleQueries(type, true);
  // Store-backed & route-scoped (see useSearchSimilarityFromRoute) so it also
  // resolves correctly when useSaveRecord runs inside TableActionProvider at
  // router root, above the search-table scope context.
  const isSimilarity = useSearchSimilarityFromRoute();

  const url =
    type === SearchType.People
      ? PEOPLE_RESOURCE_MANAGEMENT_API
      : COMPANY_RESOURCE_MANAGEMENT_API;

  const baseMutation = useBaseMutation<SaveRecordRequest, void>(
    {
      url: url.replace(':wid', String(workspaceId)).concat('?action=store'),
      method: HttpMethod.post,
      headers: { 'x-customer-id': workspaceId },
    },
    {
      onSuccess(_response, _request) {
        invalidateData();
      },
      onError(error, _request) {
        notify.closeSnackbar();
        if (error.status === HttpStatusCode.Conflict)
          notify.warning('Error', MESSAGES.ACTION_CONFLICT, undefined);
        else if (
          error.cause.code !== 402 &&
          error.cause.subCode !== ERROR_CODES.PROCESSING_EMAIL_ENRICHMENT
        )
          notify.error(error.message ?? 'An error occured.');
      },
      doNotShowErrorNotification: true,
    }
  );

  // Tag every request with the search mode. Backend contract: send Similarity
  // when a search `value` filter is present, otherwise Semantic (their default).
  return {
    ...baseMutation,
    mutateAsync: (
      request: SaveRecordRequest,
      mutateOptions?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      // Similarity when a `value` filter is set, else Semantic (backend default).
      const requestBody = {
        ...request,
        type: isSimilarity ? SearchMode.Similarity : SearchMode.Semantic,
      };

      return baseMutation.mutateAsync(requestBody, mutateOptions);
    },
    mutate: (
      request: SaveRecordRequest,
      mutateOptions?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      // Similarity when a `value` filter is set, else Semantic (backend default).
      const requestBody = {
        ...request,
        type: isSimilarity ? SearchMode.Similarity : SearchMode.Semantic,
      };

      return baseMutation.mutate(requestBody, mutateOptions);
    },
  };
}
